import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Inbox, Send, Loader2, Users } from "lucide-react";
import { toast } from "sonner";

/**
 * InboxBroadcastCard — send a promo message to every opted-in customer's
 * in-app inbox. Drops into the existing `business_promo` thread system, so
 * customers see the message alongside any one-off DMs the business has sent.
 * No external carrier — everything lives inside servrevu.
 */
export default function InboxBroadcastCard({ subscriptionActive }) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [audience, setAudience] = useState({ count: 0 });
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (!subscriptionActive) return;
    api
      .get("/me/business/broadcast-audience")
      .then(({ data }) => setAudience(data || {}))
      .catch(() => {});
  }, [subscriptionActive]);

  const send = async () => {
    const text = body.trim();
    if (text.length < 2) {
      toast.error("Write your broadcast message first");
      return;
    }
    if (
      !window.confirm(
        `Send this message to ${audience.count} opted-in customer${audience.count === 1 ? "" : "s"}? A 24h cooldown will kick in afterwards.`
      )
    )
      return;
    setSending(true);
    try {
      const { data } = await api.post("/me/business/broadcast-dm", { body: text });
      setLastResult(data);
      setBody("");
      toast.success(`Broadcast delivered to ${data.sent} inbox${data.sent === 1 ? "" : "es"}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  if (!subscriptionActive) {
    return (
      <Card className="rounded-md border border-border p-6" data-testid="inbox-broadcast-card-locked">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center shrink-0">
            <Inbox size={18} />
          </div>
          <div>
            <h3 className="font-display text-xl">Inbox broadcasts</h3>
            <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
              Requires active servrevu Pro subscription.
            </p>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="rounded-md border border-border p-6" data-testid="inbox-broadcast-card">
      <div className="flex items-start gap-3 flex-wrap">
        <div className="h-10 w-10 rounded-full bg-sky-50 text-sky-700 flex items-center justify-center shrink-0">
          <Inbox size={18} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-display text-xl">Inbox broadcasts</h3>
            <Badge
              variant="outline"
              className="rounded-sm text-[10px] font-mono border-emerald-400 text-emerald-700"
              data-testid="inbox-broadcast-badge"
            >
              IN-APP · NO CARRIER
            </Badge>
          </div>
          <p className="text-sm text-gray-500 mt-0.5 max-w-2xl">
            Drop a promo message into every opted-in customer&apos;s in-app inbox.
            No external SMS or email required — customers see it in their
            <span className="font-mono"> Messages </span> tab and can reply
            two-way. 24-hour cooldown per broadcast; respects each customer&apos;s
            category preferences.
          </p>
        </div>
      </div>

      <div className="mt-5">
        <div
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-mono text-gray-600 mb-3"
          data-testid="inbox-broadcast-audience"
        >
          <Users size={12} />
          {audience.count} customer{audience.count === 1 ? "" : "s"} reachable right now
        </div>
        <Textarea
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          maxLength={1500}
          placeholder="Fresh cold brew today only — free upsize for the first 50 orders."
          className="rounded-sm"
          data-testid="inbox-broadcast-body"
        />
        <div className="mt-1 flex items-center justify-between">
          <span className="text-[11px] font-mono text-gray-400">{body.length}/1500</span>
          <Button
            type="button"
            onClick={send}
            disabled={sending || body.trim().length < 2 || audience.count === 0}
            className="rounded-[35px] bg-[#B31E1E] hover:bg-[#8F1717]"
            data-testid="inbox-broadcast-send-button"
          >
            {sending ? (
              <>
                <Loader2 size={14} className="mr-1.5 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send size={14} className="mr-1.5" /> Send broadcast
              </>
            )}
          </Button>
        </div>
        {lastResult && (
          <p className="text-[11px] font-mono text-gray-500 mt-2">
            Last broadcast: {lastResult.sent} customer{lastResult.sent === 1 ? "" : "s"} reached
          </p>
        )}
      </div>
    </Card>
  );
}
